import { createServerFn } from '@tanstack/react-start';
import { and, count, desc, eq, ilike, or } from 'drizzle-orm';
import { z } from 'zod';

import { getDb } from '@/db/client';
import { tips, type TipRow, type TipStatus } from '@/db/schema';
import { tipsBucketId } from '@/lib/media-url';
import {
    TIPS_ALLOWED_PHOTO_EXTENSIONS,
    TIPS_BUCKET_NAME,
    TIPS_PHOTO_MAX_BYTES,
} from '@/lib/tips';
import { requireAdminAccess } from '@/server/functions/auth';
import { createAdminClient } from '@/server/lib/appwrite';

export type { TipRow, TipStatus };

const tipsBucketSettings = {
    name: TIPS_BUCKET_NAME,
    permissions: [] as string[],
    fileSecurity: false,
    enabled: true,
    maximumFileSize: TIPS_PHOTO_MAX_BYTES,
    allowedFileExtensions: [...TIPS_ALLOWED_PHOTO_EXTENSIONS],
    compression: 'gzip' as never,
    encryption: true,
    antivirus: false,
};

async function ensureTipsBucket() {
    const { storage } = createAdminClient();
    const bucketId = tipsBucketId();
    try {
        const existing = await storage.getBucket({ bucketId });
        const existingExt = [...(existing.allowedFileExtensions ?? [])].sort().join(',');
        const wantedExt = [...tipsBucketSettings.allowedFileExtensions].sort().join(',');
        if (
            Number(existing.maximumFileSize) !== TIPS_PHOTO_MAX_BYTES ||
            existingExt !== wantedExt
        ) {
            await storage.updateBucket({
                bucketId,
                ...tipsBucketSettings,
            });
        }
    } catch {
        await storage.createBucket({
            bucketId,
            ...tipsBucketSettings,
        });
    }
    return { storage, bucketId };
}

async function bufferToDataUrl(
    data: ArrayBuffer | Buffer | Uint8Array,
    mimeType = 'image/jpeg',
) {
    const buffer = Buffer.isBuffer(data)
        ? data
        : Buffer.from(data instanceof ArrayBuffer ? new Uint8Array(data) : data);
    return `data:${mimeType};base64,${buffer.toString('base64')}`;
}

const listTipsSchema = z.object({
    q: z.string().optional(),
    status: z.enum(['all', 'unread', 'read', 'archived']).optional(),
    limit: z.number().int().min(1).max(200).optional(),
    offset: z.number().int().min(0).optional(),
});

export const adminListTipsFn = createServerFn({ method: 'GET' })
    .validator(listTipsSchema)
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const limit = data.limit ?? 50;
        const offset = data.offset ?? 0;
        const filters = [];
        if (data.status && data.status !== 'all') {
            filters.push(eq(tips.status, data.status));
        }
        if (data.q?.trim()) {
            const q = `%${data.q.trim()}%`;
            filters.push(
                or(
                    ilike(tips.body, q),
                    ilike(tips.name, q),
                    ilike(tips.phone, q),
                    ilike(tips.id, q),
                )!,
            );
        }
        const where = filters.length ? and(...filters) : undefined;
        const [rows, total, unread] = await Promise.all([
            db
                .select()
                .from(tips)
                .where(where)
                .orderBy(desc(tips.createdAt))
                .limit(limit)
                .offset(offset),
            db.select({ value: count() }).from(tips).where(where),
            db
                .select({ value: count() })
                .from(tips)
                .where(eq(tips.status, 'unread')),
        ]);
        return {
            rows,
            total: total[0]?.value ?? 0,
            unread: unread[0]?.value ?? 0,
        };
    });

export const adminCountUnreadTipsFn = createServerFn({ method: 'GET' }).handler(
    async () => {
        await requireAdminAccess();
        const [row] = await getDb()
            .select({ value: count() })
            .from(tips)
            .where(eq(tips.status, 'unread'));
        return { unread: row?.value ?? 0 };
    },
);

export const adminGetTipFn = createServerFn({ method: 'GET' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const [row] = await getDb()
            .select()
            .from(tips)
            .where(eq(tips.id, data.id))
            .limit(1);
        if (!row) {
            throw new Error('הידיעה לא נמצאה');
        }

        let photoDataUrl: string | null = null;
        if (row.photoFileId) {
            try {
                const { storage, bucketId } = await ensureTipsBucket();
                const preview = await storage.getFilePreview({
                    bucketId,
                    fileId: row.photoFileId,
                    width: 1200,
                    height: 1200,
                    quality: 82,
                });
                photoDataUrl = await bufferToDataUrl(preview as ArrayBuffer, 'image/jpeg');
            } catch {
                photoDataUrl = null;
            }
        }

        return { tip: row, photoDataUrl };
    });

export const adminSetTipStatusFn = createServerFn({ method: 'POST' })
    .validator(
        z.object({
            id: z.string().min(1),
            status: z.enum(['unread', 'read', 'archived']),
        }),
    )
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [existing] = await db
            .select()
            .from(tips)
            .where(eq(tips.id, data.id))
            .limit(1);
        if (!existing) {
            throw new Error('הידיעה לא נמצאה');
        }
        const now = new Date();
        const readAt =
            data.status === 'unread'
                ? null
                : existing.readAt ?? now;
        await db
            .update(tips)
            .set({
                status: data.status,
                readAt,
                updatedAt: now,
            })
            .where(eq(tips.id, data.id));
        return { ok: true as const, status: data.status };
    });

export const adminDeleteTipFn = createServerFn({ method: 'POST' })
    .validator(z.object({ id: z.string().min(1) }))
    .handler(async ({ data }) => {
        await requireAdminAccess();
        const db = getDb();
        const [existing] = await db
            .select()
            .from(tips)
            .where(eq(tips.id, data.id))
            .limit(1);
        if (!existing) {
            return { ok: true as const };
        }
        if (existing.photoFileId) {
            try {
                const { storage, bucketId } = await ensureTipsBucket();
                await storage.deleteFile({
                    bucketId,
                    fileId: existing.photoFileId,
                });
            } catch {
                // Row still goes away even if storage cleanup fails.
            }
        }
        await db.delete(tips).where(eq(tips.id, data.id));
        return { ok: true as const };
    });
